'use client';

import { useEffect, useMemo } from 'react';
import { defaultBasketData, ROUTES } from 'constant';
import { useRouter } from 'next/navigation';
import {
  useDeleteShoppingCartItemMutation,
  useGetShoppingCartQuery,
  useUpdateShoppingCartItemMutation
} from 'services/shoppingCartApi';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
  useTheme
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setPackagesShopLoader } from 'store/reducers/packagesShopSlice';

import {
  buildLoginUrlWithReturnPath,
  formatCurrency,
  rememberCurrentPageForPostAuthLogin
} from 'utils';
import { getCartData } from 'utils/cartMapping';

import { ContentLoader } from 'components/ContentLoader';
import { Button, Divider, MultiStepHeader } from 'ui';
import { PackagesDetailsItem } from './components';
import { NavigationButtons } from '../NavigationButtons';

interface PackagesDetailsStepProps {
  onForward: () => void;
  onBack: () => void;
}

const PackagesDetailsStep = ({
  onForward,
  onBack
}: PackagesDetailsStepProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    colors: {
      palette: {
        shop: {
          packages: { stepCartDetails }
        }
      }
    }
  } = useTheme();
  const { user } = useAppSelector((state) => state.user);
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { guestInfo } = useAppSelector((state) => state.persistGuestAuth);
  const { selectedPackages } = useAppSelector((state) => state.cart);

  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);
  const isGuestLogged = Boolean(guestInfo?.clid) && Boolean(guestInfo?.token);
  const isAuthenticated = isLogged || isGuestLogged;

  const { data: shopData = defaultBasketData } = useGetShoppingCartQuery('', {
    skip: !isAuthenticated
  });

  const [deleteShoppingCartItem, { isLoading: isDeleting }] =
    useDeleteShoppingCartItemMutation();
  const [updateShoppingCartItem, { isLoading: isUpdating }] =
    useUpdateShoppingCartItemMutation();

  const localCartData = useMemo(
    () => getCartData(isAuthenticated, shopData, [], selectedPackages),
    [isAuthenticated, shopData, selectedPackages]
  );
  const cartData = isAuthenticated ? shopData : localCartData;
  const isPackagesExist = cartData.items.length > 0;

  useEffect(() => {
    const isLoading = isDeleting || isUpdating;

    dispatch(setPackagesShopLoader(isLoading));
  }, [dispatch, isDeleting, isUpdating]);

  const toLoginPage = () => {
    const returnPath = `${window.location.pathname}${window.location.search}`;
    rememberCurrentPageForPostAuthLogin();
    router.push(buildLoginUrlWithReturnPath(ROUTES.login, returnPath));
  };

  useEffect(() => {
    if (!isPackagesExist) {
      onBack();
    }
  }, [isPackagesExist, onBack]);

  const onNextPage = isAuthenticated ? onForward : toLoginPage;

  return (
    <Box>
      <MultiStepHeader
        title='Cart Details'
        headingColor={stepCartDetails.heading}
        mt={0}
        mb={{ base: '20px', md: user ? '40px' : '20px' }}
      />

      {!user && (
        <Flex
          justifyContent='space-between'
          alignItems='center'
          gap='20px'
          py='15px'
          px='18px'
          bg={stepCartDetails.signInBg}
        >
          <Text
            fontSize={{ base: '14px', md: '18px', lg: '21px' }}
            color={stepCartDetails.signInText}
          >
            Sign In to see final pricing
          </Text>

          <Button
            onClick={toLoginPage}
            variant='brand'
            colorScheme='secondary'
            border='1px solid'
            borderColor={stepCartDetails.signInButton.border}
            borderRadius='6px'
            h={{ base: '48px', lg: '60px' }}
            w={{ base: 'auto', md: '130px', lg: '200px' }}
            bg='transparent'
            fontSize={{ base: '14px', md: '18px', lg: '21px' }}
          >
            Sign In
          </Button>
        </Flex>
      )}
      <Box position='relative' w='100%'>
        {isPackagesExist ? (
          <Box>
            <Divider
              bg={stepCartDetails.list.divider}
              height='2px'
              border='none'
            />
            {/* Table header */}
            <Grid
              templateColumns='repeat(12, 1fr)'
              py='10px'
              columnGap='10px'
              display={{ base: 'none', lg: 'grid' }}
            >
              <GridItem colSpan={5}>
                <Text
                  fontSize='12px'
                  color={stepCartDetails.list.packageText}
                  textTransform='uppercase'
                >
                  Package
                </Text>
              </GridItem>

              <GridItem colSpan={2}>
                <Text
                  fontSize='12px'
                  color={stepCartDetails.list.amountText}
                  textTransform='uppercase'
                  textAlign='end'
                >
                  Amount
                </Text>
              </GridItem>

              <GridItem colSpan={3}>
                <Text
                  fontSize='12px'
                  color={stepCartDetails.list.qtyText}
                  textTransform='uppercase'
                  textAlign='center'
                >
                  QTY
                </Text>
              </GridItem>

              <GridItem colSpan={2}>
                <Text
                  fontSize='12px'
                  color={stepCartDetails.list.totalText}
                  textTransform='uppercase'
                  textAlign='end'
                >
                  Total
                </Text>
              </GridItem>
            </Grid>

            {cartData.items.map((item) => (
              <PackagesDetailsItem
                key={item.id}
                item={item}
                deleteShoppingCartItem={deleteShoppingCartItem}
                updateShoppingCartItem={updateShoppingCartItem}
              />
            ))}

            <Divider
              bg={stepCartDetails.list.divider}
              height='2px'
              border='none'
            />

            {/* Table footer */}
            <Stack gap={0}>
              <Stack position='relative' w='100%' py='10px' gap={0}>
                <Grid
                  templateColumns='repeat(12, 1fr)'
                  py='10px'
                  columnGap={{ base: 0, lg: '10px' }}
                  display={{ base: 'flex', lg: 'grid' }}
                >
                  <GridItem colSpan={8} />
                  <GridItem colSpan={1} width='full' alignContent='center'>
                    <Text
                      fontSize={{ base: '18px', md: '20px' }}
                      textTransform='uppercase'
                      color={stepCartDetails.list.taxText}
                      textAlign={{ base: 'start', lg: 'right' }}
                    >
                      Tax
                    </Text>
                  </GridItem>
                  <GridItem colSpan={1} />

                  <GridItem colSpan={2}>
                    <Text
                      fontSize={['20px', '23px', '25px']}
                      textTransform='uppercase'
                      color={stepCartDetails.list.taxText}
                      textAlign='right'
                    >
                      {formatCurrency(cartData.details?.tax_cost ?? 0)}
                    </Text>
                  </GridItem>
                </Grid>

                <Divider
                  position='absolute'
                  bottom={0}
                  bg={stepCartDetails.list.innerDivider}
                  height='1px'
                  border='none'
                />
              </Stack>

              <Stack position='relative' w='100%' py='10px' gap={0}>
                <Grid
                  templateColumns='repeat(12, 1fr)'
                  py='10px'
                  columnGap={{ base: 0, lg: '10px' }}
                  display={{ base: 'flex', lg: 'grid' }}
                >
                  <GridItem colSpan={6} />
                  <GridItem colSpan={3} width='full' alignContent='center'>
                    <Text
                      fontSize={{ base: '18px', md: '20px' }}
                      textTransform='uppercase'
                      color={stepCartDetails.list.subtotalText}
                      textAlign={{ base: 'start', lg: 'right' }}
                    >
                      Subtotal
                    </Text>
                  </GridItem>

                  <GridItem colSpan={3}>
                    <Text
                      fontSize={{ base: '28px', lg: '32px' }}
                      textTransform='uppercase'
                      color={stepCartDetails.list.subtotalText}
                      textAlign='end'
                    >
                      {formatCurrency(cartData.details?.total_cost ?? 0)}
                    </Text>
                  </GridItem>
                </Grid>

                <Divider
                  position='absolute'
                  bottom={0}
                  bg={stepCartDetails.list.innerDivider}
                  height='1px'
                  border='none'
                />
              </Stack>
            </Stack>
          </Box>
        ) : (
          <ContentLoader label='No packages available' isLoading={false} />
        )}
      </Box>

      <NavigationButtons
        onBackHandler={onBack}
        onNextHandler={onNextPage}
        backLabel='Continue shopping'
        nextLabel='Checkout'
      />
    </Box>
  );
};

export default PackagesDetailsStep;
